import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import {
  CheckOutlined,
} from '@ant-design/icons';
import { ReactComponent as IconFuzzyMapping } from 'assets/images/icons/iconFuzzyMapping.svg';
import { ActiveStepSpinner } from 'components/Custom/ActiveStepSpinner';
import { AiMappingStatus } from 'store/returns/types';

const STEP_COUNT = 4;
const STEP_ADVANCE_MS = [6000, 10000, 15000];
const SUCCESS_DISMISS_MS = 3000;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Overlay = styled.div<{ $contained?: boolean }>`
  position: ${(p) => (p.$contained ? 'absolute' : 'fixed')};
  inset: 0;
  z-index: ${(p) => (p.$contained ? 10 : 1100)};
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(4, 42, 42, 0.35);
  padding: 24px;
`;

const Panel = styled.div.attrs({
  className: 'mapping-form-root',
})`
  font-family: var(--font-family, Inter, sans-serif);
  position: relative;
  width: min(420px, 100%);
  background: var(--color-surface-raised, #ffffff);
  border: 1px solid var(--color-border, #dbeae3);
  border-radius: 12px;
  box-shadow: 0 12px 40px rgba(4, 42, 42, 0.12);
  padding: 28px 28px 24px;
  animation: ${fadeIn} 0.25s ease-out;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`;

const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--color-status-warning-bg, #fff5e2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
  color: var(--color-text-dark, #042a2a);
`;

const Subtitle = styled.div`
  font-size: 13px;
  line-height: 20px;
  color: var(--color-text-muted, #5a6a6a);
  margin-top: 2px;
`;

const StepsList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const StepRow = styled.li<{ $state: 'pending' | 'active' | 'complete' }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: ${(props) =>
    props.$state === 'pending'
      ? 'var(--color-text-muted, #5a6a6a)'
      : props.$state === 'active'
      ? 'var(--color-text-dark, #042a2a)'
      : '#41aaaf'};
  font-size: 14px;
  line-height: 20px;
  font-weight: ${(props) => (props.$state === 'active' ? 600 : 400)};
  transition: color 0.25s ease;
`;

const StepIndicator = styled.div<{ $state: 'pending' | 'active' | 'complete' }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  border: 2px solid
    ${(props) =>
      props.$state === 'complete'
        ? 'var(--color-primary, #41aaaf)'
        : props.$state === 'active'
        ? '#ffbc3e'
        : 'var(--color-border, #dbeae3)'};
  background: ${(props) =>
    props.$state === 'complete'
      ? 'var(--color-layout-bg, #e9f6f7)'
      : props.$state === 'active'
      ? '#fff5e2'
      : 'var(--color-surface, #f3f7f8)'};
  color: ${(props) =>
    props.$state === 'complete'
      ? '#41aaaf'
      : props.$state === 'active'
      ? 'var(--color-text-dark, #042a2a)'
      : 'var(--color-text-muted, #5a6a6a)'};
`;

const ReviewNotice = styled.div`
  margin-top: 20px;
  padding: 12px 14px;
  border-radius: 8px;
  background: var(--color-status-warning-bg, #fff5e2);
  border: 1px solid var(--color-border, #dbeae3);
  color: var(--color-text-dark, #042a2a);
  font-size: 12px;
  line-height: 18px;
`;

const SuccessBanner = styled.div`
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 8px;
  background: var(--color-layout-bg, #e9f6f7);
  border: 1px solid var(--color-primary, #41aaaf);
  color: #41aaaf;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: ${fadeIn} 0.25s ease-out;
`;

const STEP_KEYS = [
  'returns.mapping.ai-mapping.step-reading-rates',
  'returns.mapping.ai-mapping.step-analysing-return',
  'returns.mapping.ai-mapping.step-suggesting-mappings',
  'returns.mapping.ai-mapping.step-applying-results',
] as const;

interface AiMappingStepsOverlayProps {
  status: AiMappingStatus;
  onDismiss: () => void;
  /** When true, overlay fills a positioned parent instead of the viewport. */
  contained?: boolean;
  /** When true, backdrop click closes the overlay during loading. */
  dismissible?: boolean;
  labels?: AiMappingLabels;
}

export interface AiMappingLabels {
  readingStep?: string;
  reviewNotice?: string;
  success?: string;
}

export const AiMappingStepsOverlay: React.FC<AiMappingStepsOverlayProps> = ({
  status,
  onDismiss,
  contained = false,
  dismissible = false,
  labels,
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(0);
  const [showSuccess, setShowSuccess] = React.useState(false);
  const onDismissRef = React.useRef(onDismiss);
  const dismissedRef = React.useRef(false);

  onDismissRef.current = onDismiss;

  const dismissOverlay = React.useCallback(() => {
    dismissedRef.current = true;
    setVisible(false);
    setShowSuccess(false);
    onDismissRef.current();
  }, []);

  React.useEffect(() => {
    if (status === 'loading') {
      dismissedRef.current = false;
      setVisible(true);
      setShowSuccess(false);
      setActiveStep(0);
      return undefined;
    }

    if (status === 'success') {
      if (dismissedRef.current) {
        onDismissRef.current();
        return undefined;
      }
      setVisible(true);
      setActiveStep(STEP_COUNT);
      setShowSuccess(true);
      const timer = window.setTimeout(() => {
        dismissedRef.current = true;
        setVisible(false);
        setShowSuccess(false);
        onDismissRef.current();
      }, SUCCESS_DISMISS_MS);
      return () => window.clearTimeout(timer);
    }

    setVisible(false);
    setShowSuccess(false);
    return undefined;
  }, [status]);

  React.useEffect(() => {
    if (status !== 'loading') return undefined;

    const timers = STEP_ADVANCE_MS.map((delay, index) =>
      window.setTimeout(() => setActiveStep(index + 1), delay)
    );

    return () => timers.forEach(window.clearTimeout);
  }, [status]);

  if (!visible) return null;

  const getStepState = (index: number): 'pending' | 'active' | 'complete' => {
    if (showSuccess || index < activeStep) return 'complete';
    if (index === activeStep) return 'active';
    return 'pending';
  };

  const overlay = (
    <Overlay
      $contained={contained}
      aria-busy={status === 'loading'}
      aria-live="polite"
      onClick={
        dismissible
          ? (e) => {
              if (e.target === e.currentTarget) dismissOverlay();
            }
          : undefined
      }
    >
      <Panel onClick={(e) => e.stopPropagation()}>
        <Header>
          <HeaderIcon>
            <IconFuzzyMapping style={{ width: 22, height: 22 }} />
          </HeaderIcon>
          <div>
            <Title>{t('returns.mapping.ai-mapping.title')}</Title>
            <Subtitle>{t('returns.mapping.ai-mapping.subtitle')}</Subtitle>
          </div>
        </Header>

        <StepsList>
          {STEP_KEYS.map((key, index) => {
            const stepState = getStepState(index);
            return (
              <StepRow key={key} $state={stepState}>
                <StepIndicator $state={stepState}>
                  {stepState === 'complete' ? (
                    <CheckOutlined style={{ fontSize: 13 }} />
                  ) : stepState === 'active' ? (
                    <ActiveStepSpinner />
                  ) : (
                    index + 1
                  )}
                </StepIndicator>
                <span>
                  {index === 0 && labels?.readingStep ? labels.readingStep : t(key)}
                </span>
              </StepRow>
            );
          })}
        </StepsList>

        <ReviewNotice>
          {labels?.reviewNotice ?? t('returns.mapping.ai-mapping.review-notice')}
        </ReviewNotice>

        {showSuccess && (
          <SuccessBanner>
            <CheckOutlined />
            {labels?.success ?? t('returns.mapping.ai-mapping.success')}
          </SuccessBanner>
        )}
      </Panel>
    </Overlay>
  );

  if (contained) return overlay;
  return createPortal(overlay, document.body);
};
